import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { add, format, isToday, isTomorrow } from "date-fns";

import { useGetRestaurantQuery } from "../../hooks/useGetRestaurantQuery";
import { FullScreenSpinner } from "../../tabin/components/fullScreenSpinner";
import { PageWrapper } from "../../tabin/components/pageWrapper";
import { Button } from "../../tabin/components/button";
import { Input } from "../../tabin/components/input";
import { TextArea } from "../../tabin/components/textArea";
import { Select } from "../../tabin/components/select";
import { Stepper } from "../../tabin/components/stepper";
import { CachedImage } from "../../tabin/components/cachedImage";
import { getCloudFrontDomainName } from "../../private/aws-custom";
import { getIsRestaurantOpen, getRestaurantTimings } from "../../util/util";
import { getReservationPauseDate, isReservationTimeAvailable } from "../../util/reservationAvailability";
import { IRestaurantTimings } from "../../model/model";
import {
    GET_RESERVATION_PAUSE_STATUS,
    IGET_RESTAURANT_OPERATING_HOURS,
    GET_RESERVATIONS_BY_RESTAURANT_BY_DATE,
    EReservationStatus,
    IGET_RESERVATION,
} from "../../graphql/customQueries";
import { CREATE_RESERVATION } from "../../graphql/customMutations";
import { reservationConfirmedPath } from "../main";

import "./reservation.scss";

const BOOKING_INTERVAL_MINUTES = 15;
const MAX_COVERS = 20;
const BOOKING_WINDOW_DAYS = 60;

interface IAvailableDay {
    dateValue: string;
    dateLabel: string;
    day: number;
    isOpen: boolean;
    timings: IRestaurantTimings[];
}

interface IAvailableTime {
    timeValue: string;
    timeLabel: string;
    bookedCovers: number;
    maxCoversPerTimeSlot: number | null;
    disabled: boolean;
}

export const Reservation = () => {
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const navigate = useNavigate();

    const { data: restaurant, loading, error } = useGetRestaurantQuery(restaurantId || "");

    const {
        data: pauseData,
        loading: pauseLoading,
        error: pauseError,
        refetch: refetchPause,
    } = useQuery(GET_RESERVATION_PAUSE_STATUS, {
        variables: { restaurantId },
        skip: !restaurantId,
        fetchPolicy: "network-only",
        pollInterval: 30000,
    });
    const pauseUntil = pauseData?.getRestaurant ? pauseData.getRestaurant.pauseReservationsUntil : restaurant?.pauseReservationsUntil;
    const [now, setNow] = useState(Date.now());
    const [checking, setChecking] = useState(false);
    const [timesLoading, setTimesLoading] = useState(false);
    const timesRequest = useRef(0);

    // Step 1: date/time/covers
    const [covers, setCovers] = useState(2);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [availableDays, setAvailableDays] = useState<IAvailableDay[]>([]);
    const [availableTimes, setAvailableTimes] = useState<IAvailableTime[]>([]);

    // Step 2: guest details
    const [step, setStep] = useState<1 | 2>(1);
    const [guestName, setGuestName] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    const [guestPhone, setGuestPhone] = useState("");
    const [guestNotes, setGuestNotes] = useState("");
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [getReservations] = useLazyQuery(GET_RESERVATIONS_BY_RESTAURANT_BY_DATE);
    const [createReservation, { loading: submitting }] = useMutation(CREATE_RESERVATION);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 30000);
        return () => window.clearInterval(timer);
    }, []);

    // A pause applied while entering guest details must send the customer back to time selection.
    useEffect(() => {
        if (selectedDate && selectedTime && !isReservationTimeAvailable(new Date(`${selectedDate}T${selectedTime}`), pauseUntil)) {
            setStep(1);
        }
    }, [pauseUntil, now, selectedDate, selectedTime]);

    // Build available days when restaurant loads
    useEffect(() => {
        if (!restaurant) return;
        const days = buildAvailableDays(restaurant.operatingHours);
        setAvailableDays(days);
        const firstAvailable = days.find((d) => d.isOpen);
        setSelectedDate((current) => {
            const currentDayAvailable = days.some((day) => day.dateValue === current && day.isOpen);
            return currentDayAvailable ? current : firstAvailable?.dateValue ?? "";
        });
    }, [restaurant, pauseUntil, now]);

    // Recalculate available times whenever date or covers change
    useEffect(() => {
        if (!selectedDate || availableDays.length === 0) {
            setAvailableTimes([]);
            setSelectedTime("");
            setTimesLoading(false);
            return;
        }
        loadTimesForDate(selectedDate);

        return () => {
            timesRequest.current += 1;
        };
    }, [selectedDate, covers, availableDays, pauseUntil]);

    const buildAvailableDays = (operatingHours: IGET_RESTAURANT_OPERATING_HOURS): IAvailableDay[] => {
        const now = new Date();
        const days: IAvailableDay[] = [];

        for (let i = 0; i < BOOKING_WINDOW_DAYS; i++) {
            const date = add(now, { days: i });
            const day = date.getDay();
            const dateValue = format(date, "yyyy-MM-dd");
            const venueOpen = getIsRestaurantOpen(operatingHours, day);
            const timings = getRestaurantTimings(operatingHours, date, day, BOOKING_INTERVAL_MINUTES);
            const futureTimings = timings.filter((timing) => timing.interval > now);
            const isOpen = venueOpen && futureTimings.some((timing) => isReservationTimeAvailable(timing.interval, pauseUntil, now));

            let dateLabel = isToday(date) ? "Today" : isTomorrow(date) ? "Tomorrow" : format(date, "eee, do MMM");
            if (!isOpen) dateLabel += venueOpen && futureTimings.length > 0 ? " (PAUSED)" : " (CLOSED)";

            days.push({ dateValue, dateLabel, day, isOpen, timings });
        }

        return days;
    };

    const loadTimesForDate = async (date: string) => {
        const day = availableDays.find((d) => d.dateValue === date);
        if (!day) return;
        const request = ++timesRequest.current;
        setTimesLoading(true);
        setAvailableTimes([]);

        // Fetch existing reservations for this date to check covers availability
        let reservationsForDate: IGET_RESERVATION[] = [];
        try {
            const result = await getReservations({
                variables: {
                    restaurantId,
                    date: { eq: date },
                    limit: 500,
                },
            });
            reservationsForDate = (result.data?.getReservationsByRestaurantByDate?.items ?? []).filter(
                (r: IGET_RESERVATION) => r.status !== EReservationStatus.CANCELLED,
            );
        } catch (e) {
            console.error("Error fetching reservations for date:", e);
        }

        if (request !== timesRequest.current) return;

        // Build covers-per-slot map
        const bookedCoversBySlot: Record<string, number> = {};
        reservationsForDate.forEach((r) => {
            const slotKey = roundTimeToSlot(r.time);
            bookedCoversBySlot[slotKey] = (bookedCoversBySlot[slotKey] ?? 0) + r.covers;
        });

        const now = new Date();
        const times: IAvailableTime[] = [];

        day.timings.forEach((t) => {
            if (!day.isOpen || !isReservationTimeAvailable(t.interval, pauseUntil, now)) return;

            const timeValue = format(t.interval, "HH:mm:ss");
            const timeLabel = format(t.interval, "hh:mm a");
            const bookedCovers = bookedCoversBySlot[timeValue] ?? 0;
            const maxCovers = t.maxCoversPerTimeSlot;

            const isFull = maxCovers !== null && bookedCovers + covers > maxCovers;

            times.push({
                timeValue,
                timeLabel,
                bookedCovers,
                maxCoversPerTimeSlot: maxCovers,
                disabled: isFull,
            });
        });

        setAvailableTimes(times);

        // Auto-select first available time
        const firstAvailable = times.find((t) => !t.disabled);
        setSelectedTime((current) => {
            const currentTimeAvailable = times.some((time) => time.timeValue === current && !time.disabled);
            return currentTimeAvailable ? current : firstAvailable?.timeValue ?? "";
        });
        setTimesLoading(false);
    };

    const roundTimeToSlot = (time: string): string => {
        // time is "HH:mm:ss" — round down to nearest 15 min
        const [h, m] = time.split(":").map(Number);
        const roundedM = Math.floor(m / BOOKING_INTERVAL_MINUTES) * BOOKING_INTERVAL_MINUTES;
        return `${String(h).padStart(2, "0")}:${String(roundedM).padStart(2, "0")}:00`;
    };

    const validateStep2 = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!guestName.trim()) newErrors.guestName = "Name is required";
        if (!guestEmail.trim()) {
            newErrors.guestEmail = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
            newErrors.guestEmail = "Enter a valid email address";
        }
        if (!guestPhone.trim()) newErrors.guestPhone = "Phone number is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirm = async () => {
        if (checking || submitting || timesLoading || !validateStep2()) return;
        setChecking(true);

        try {
            // Recheck the server so an already-open form respects a newly applied pause.
            const latest = await refetchPause();
            const latestRestaurant = latest.data?.getRestaurant;
            if (!latestRestaurant) throw new Error("Restaurant status unavailable");
            const bookingTime = new Date(`${selectedDate}T${selectedTime}`);
            if (
                !availableTimes.some((time) => time.timeValue === selectedTime && !time.disabled) ||
                !isReservationTimeAvailable(bookingTime, latestRestaurant.pauseReservationsUntil)
            ) {
                setErrors({ submit: "This time is no longer available. Please choose another time." });
                setStep(1);
                setNow(Date.now());
                return;
            }
            const result = await createReservation({
                variables: {
                    restaurantId,
                    date: selectedDate,
                    time: selectedTime,
                    covers,
                    status: EReservationStatus.CONFIRMED,
                    customerName: guestName.trim(),
                    customerEmail: guestEmail.trim() || null,
                    customerPhone: guestPhone.trim() || null,
                    notes: guestNotes.trim() || null,
                },
            });

            const reservation = result.data?.createReservation;
            if (!reservation) throw new Error("No reservation returned");

            navigate(`/${restaurantId}${reservationConfirmedPath}`, {
                state: {
                    reservationId: reservation.id,
                    date: selectedDate,
                    time: selectedTime,
                    covers,
                    customerName: guestName.trim(),
                    restaurantName: restaurant?.name,
                },
            });
        } catch (e) {
            console.error("Error creating reservation:", e);
            setErrors({ submit: "Something went wrong. Please try again." });
        } finally {
            setChecking(false);
        }
    };

    if (loading) return <FullScreenSpinner show text="Loading..." />;
    if (error || !restaurant) return <div className="reservation-error">Restaurant not found. Please check the link and try again.</div>;
    if (!restaurant.verified) return <div className="reservation-error">This restaurant is not available for bookings.</div>;

    const selectedDayData = availableDays.find((d) => d.dateValue === selectedDate);
    const selectedTimeData = availableTimes.find((t) => t.timeValue === selectedTime);
    const noTimesAvailable = availableTimes.length === 0 || availableTimes.every((t) => t.disabled);
    const selectedTimeAvailable =
        !!selectedTimeData && !selectedTimeData.disabled && isReservationTimeAvailable(new Date(`${selectedDate}T${selectedTime}`), pauseUntil);
    const pauseDate = getReservationPauseDate(pauseUntil);
    const paused = pauseDate && pauseDate.getTime() > now;

    const stepIndicator = (
        <div className="reservation-steps">
            <div className={`reservation-step ${step === 1 ? "active" : "complete"}`}>
                <div className="reservation-step-circle">{step > 1 ? "✓" : "1"}</div>
                <span>When</span>
            </div>
            <div className="reservation-step-divider" />
            <div className={`reservation-step ${step === 2 ? "active" : ""}`}>
                <div className="reservation-step-circle">2</div>
                <span>Your Details</span>
            </div>
        </div>
    );

    const step1 = (
        <div className="reservation-form">
            <div className="reservation-field">
                <label className="reservation-label">Party Size</label>
                <Stepper count={covers} setCount={setCovers} min={1} max={MAX_COVERS} size={40} className="reservation-stepper">
                    <span className="reservation-stepper-label">{covers === 1 ? "1 guest" : `${covers} guests`}</span>
                </Stepper>
            </div>

            <div className="reservation-field">
                <label className="reservation-label">Date</label>
                <Select name="reservation-date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>
                    {availableDays.map((day) => (
                        <option key={day.dateValue} value={day.dateValue} disabled={!day.isOpen}>
                            {day.dateLabel}
                        </option>
                    ))}
                </Select>
            </div>

            <div className="reservation-field">
                <label className="reservation-label">Time</label>
                {timesLoading ? (
                    <div className="reservation-no-times">Checking availability...</div>
                ) : noTimesAvailable ? (
                    <div className="reservation-no-times">No availability for this date. Please select another day.</div>
                ) : (
                    <Select name="reservation-time" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)}>
                        {availableTimes.map((t) => {
                            const remaining = t.maxCoversPerTimeSlot !== null ? t.maxCoversPerTimeSlot - t.bookedCovers : null;
                            const label = t.disabled
                                ? `${t.timeLabel} (Full)`
                                : remaining !== null && remaining <= 10
                                ? `${t.timeLabel} (${remaining} spots left)`
                                : t.timeLabel;
                            return (
                                <option key={t.timeValue} value={t.timeValue} disabled={t.disabled}>
                                    {label}
                                </option>
                            );
                        })}
                    </Select>
                )}
            </div>

            <Button
                className="reservation-cta-button"
                disabled={!selectedDate || !selectedTimeAvailable || noTimesAvailable || timesLoading || pauseLoading || !!pauseError}
                onClick={() => setStep(2)}
            >
                Continue
            </Button>
        </div>
    );

    const step2 = (
        <div className="reservation-form">
            <div className="reservation-summary-bar">
                <span>{selectedDayData ? selectedDayData.dateLabel : selectedDate}</span>
                <span className="reservation-summary-dot">·</span>
                <span>{selectedTimeData?.timeLabel}</span>
                <span className="reservation-summary-dot">·</span>
                <span>{covers === 1 ? "1 guest" : `${covers} guests`}</span>
                <button className="reservation-edit-link" onClick={() => setStep(1)}>
                    Edit
                </button>
            </div>

            <div className="reservation-field">
                <Input label="Full Name" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Jane Smith" error={errors.guestName} />
            </div>

            <div className="reservation-field">
                <Input
                    label="Email"
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="jane@example.com"
                    error={errors.guestEmail}
                />
            </div>

            <div className="reservation-field">
                <Input
                    label="Phone Number"
                    type="tel"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    placeholder="+64 21 000 0000"
                    error={errors.guestPhone}
                />
            </div>

            <div className="reservation-field">
                <TextArea
                    label="Special Requests (optional)"
                    value={guestNotes}
                    onChange={(e) => setGuestNotes(e.target.value)}
                    placeholder="Allergies, high chair needed, birthday celebration..."
                />
            </div>

            <div className="reservation-actions">
                <button className="reservation-back-button" onClick={() => setStep(1)}>
                    Back
                </button>
                <Button
                    className="reservation-cta-button"
                    disabled={checking || submitting || timesLoading || !selectedTimeAvailable || pauseLoading || !!pauseError}
                    onClick={handleConfirm}
                >
                    {checking || submitting ? "Confirming..." : "Confirm Reservation"}
                </Button>
            </div>
        </div>
    );

    return (
        <PageWrapper>
            <div className="reservation-page">
                <div className="reservation-container">
                    {restaurant.logo && (
                        <div className="reservation-logo-wrapper">
                            <CachedImage
                                url={`${getCloudFrontDomainName()}/protected/${restaurant.logo.identityPoolId}/${restaurant.logo.key}`}
                                className="reservation-logo"
                                alt="restaurant-logo"
                            />
                        </div>
                    )}

                    <h1 className="reservation-restaurant-name">{restaurant.name}</h1>
                    <h2 className="reservation-title">Reserve a Table</h2>

                    {paused && (
                        <div className="reservation-no-times mb-3" role="status">
                            Reservations are paused until {format(pauseDate, "dd MMM h:mm a")}. Please choose a time at or after the pause ends.
                        </div>
                    )}
                    {pauseError && <div className="text-error mb-3">Unable to check reservation availability. Please refresh and try again.</div>}
                    {errors.submit && (
                        <div className="text-error mb-3" role="alert">
                            {errors.submit}
                        </div>
                    )}
                    {step === 2 && !timesLoading && !selectedTimeAvailable && (
                        <div className="text-error mb-3">This time is no longer available. Please go back and choose another time.</div>
                    )}
                    {stepIndicator}

                    {step === 1 ? step1 : step2}
                </div>
            </div>
        </PageWrapper>
    );
};
